<script setup lang="ts">
import { computed } from "vue";
import type { CountdownStyleProps } from "./shared";
import { resolveLayoutTokens, speedDuration } from "./shared";

const props = defineProps<CountdownStyleProps>();
const layout = computed(() => resolveLayoutTokens(props.block, props.viewport));
const tickMs = computed(() => speedDuration(props.block.animationSpeed, 700));

const containerStyle = computed(
  () =>
    ({
      background:
        props.block.backgroundColor ||
        "radial-gradient(circle at 30% 20%, #1c1410 0%, #050302 70%)",
      fontFamily:
        props.block.fontFamily ||
        "'Playfair Display', 'Cormorant Garamond', serif",
      padding: `${layout.value.padding}px`,
      gap: `${layout.value.gap}px`,
      flexDirection: layout.value.stack ? "column" : "row",
      minHeight: layout.value.fullscreen ? "60vh" : undefined,
      "--cd-tick": `${tickMs.value}ms`,
    }) as Record<string, string | number | undefined>,
);

const cardSize = computed(() => {
  const base = props.block.digitFontSize * layout.value.digitScale;
  return {
    width: `${base * 2.4}px`,
    height: `${base * 2.6}px`,
    fontSize: `${base * 1.2}px`,
    radius: `${props.block.cardRadius ?? 8}px`,
  };
});
</script>

<template>
  <div class="cd-gold" :style="containerStyle">
    <div class="cd-gold__marble" aria-hidden="true" />
    <div
      v-for="(seg, i) in segments"
      :key="seg.key"
      class="cd-gold__cell"
      :style="{
        width: cardSize.width,
        height: cardSize.height,
        borderRadius: cardSize.radius,
        animationDelay: `${i * 150}ms`,
      }"
    >
      <div class="cd-gold__frame" />
      <div class="cd-gold__shine" />
      <span
        class="cd-gold__digit"
        :key="seg.value"
        :style="{ fontSize: cardSize.fontSize }"
      >
        {{ seg.value }}
      </span>
      <span
        class="cd-gold__label"
        :style="{ fontSize: `${block.labelFontSize}px` }"
      >
        {{ seg.label }}
      </span>
    </div>
  </div>
</template>

<style scoped>
.cd-gold {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  overflow: hidden;
  isolation: isolate;
  color: #f8e9b0;
}
.cd-gold__marble {
  position: absolute;
  inset: 0;
  background:
    radial-gradient(
      ellipse at 20% 10%,
      rgba(212, 175, 55, 0.12),
      transparent 55%
    ),
    radial-gradient(
      ellipse at 80% 90%,
      rgba(212, 175, 55, 0.08),
      transparent 55%
    ),
    radial-gradient(circle at 50% 50%, transparent 40%, rgba(0, 0, 0, 0.6) 100%);
  pointer-events: none;
  z-index: 0;
}
.cd-gold__cell {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #14110d, #0a0805 70%, #14110d);
  box-shadow:
    0 18px 40px rgba(0, 0, 0, 0.6),
    inset 0 0 40px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  animation: cd-gold-rise 800ms cubic-bezier(0.16, 1, 0.3, 1) backwards;
  transition:
    transform 0.4s ease,
    box-shadow 0.4s ease;
}
.cd-gold__cell:hover {
  transform: translateY(-4px);
  box-shadow:
    0 28px 50px rgba(0, 0, 0, 0.7),
    inset 0 0 40px rgba(212, 175, 55, 0.15);
}
@keyframes cd-gold-rise {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
.cd-gold__frame {
  position: absolute;
  inset: 6px;
  border-radius: inherit;
  border: 1px solid;
  border-image: linear-gradient(
      135deg,
      #d4af37 0%,
      #fff8d1 25%,
      #d4af37 50%,
      #8a6c1c 75%,
      #d4af37 100%
    )
    1;
  pointer-events: none;
  filter: drop-shadow(0 0 6px rgba(212, 175, 55, 0.35));
}
.cd-gold__shine {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    115deg,
    transparent 30%,
    rgba(255, 240, 180, 0.18) 48%,
    rgba(255, 240, 180, 0.04) 52%,
    transparent 70%
  );
  background-size: 200% 200%;
  background-position: 200% 0;
  animation: cd-gold-sheen 5s ease-in-out infinite;
  pointer-events: none;
}
@keyframes cd-gold-sheen {
  0%,
  100% {
    background-position: 200% 0;
  }
  50% {
    background-position: -50% 0;
  }
}
.cd-gold__digit {
  position: relative;
  z-index: 2;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  background: linear-gradient(
    180deg,
    #fff8c5 0%,
    #ecc966 25%,
    #d4af37 50%,
    #8c6f24 75%,
    #fce39c 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  display: inline-block;
  animation: cd-gold-soft var(--cd-tick, 700ms) ease-out;
}
@keyframes cd-gold-soft {
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
.cd-gold__label {
  position: relative;
  z-index: 2;
  margin-top: 8px;
  font-style: italic;
  letter-spacing: 0.4em;
  text-transform: uppercase;
  font-weight: 500;
  color: rgba(212, 175, 55, 0.8);
}
</style>
